import React, { useEffect, useState, useRef } from 'react';
import Button from '../react-elements/ButtonReact';
import MenuSection from './MenuSection';
import faqCatIcon from '../../svgs/faq-cat-icon.svg';
import plusIcon from '../../svgs/footer-mobile-expand.svg';
import popupCloseIcon from '../../svgs/popup-close.svg';

import './Menus.css'
import Spinner from '../react-elements/Spinner';

type MenuItemTag = { 
    id: number; 
    name: string; 
    slug: string; 
    display_on_menu_items: boolean;
    display_label: string | null 
};
type MenuType = { 
    id: number; 
    name: string; 
    slug: string 
};
type MenuSectionType = { 
    id: number; 
    title: string; 
    display_name: string; 
    menu_types: MenuType[];
    favourites: MenuItem[];
    menu_content: { section_title: string; section_menu_items: MenuItem[] }[];
};
type MenuItem = {
    id: number;
    name: string;
    description: string;
    image: number;
    tags: { id: number; name: string; slug: string }[];
};

// Update the component props
const Menus = ({ module, menuData }: { module: any; menuData: { menus: MenuSectionType[]; menu_item_tags: MenuItemTag[]; menu_types: MenuType[] } }) => {
    const [allMenuItemTags, setAllMenuItemTags] = useState<MenuItemTag[]>([]);
    const [allMenuSectionTypes, setAllMenuSectionTypes] = useState<MenuType[]>([]);
    const [allMenuSections, setAllMenuSections] = useState<MenuSectionType[]>([]);
    const [menuSectionsByType, setMenuSectionsByType] = useState([]);
    const [activeMenuSectionId, setActiveMenuSectionId] = useState<number | null>(null);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [activeMenuSectionType, setActiveMenuSectionType] = useState<number | null>(null);
    const [isDietaryPopupActive, setIsDietaryPopupActive] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [headerOpacity, setHeaderOpacity] = useState(1); // New state for header opacity

    // Refs for each menu section
    const sectionRefs = useRef<{ [key: number]: HTMLElement | null }>({});
    const dietaryPopupRef = useRef<HTMLDivElement | null>(null);
    const openDietaryButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const initializeMenuData = async () => {
            setIsLoading(true);
            try {
                setAllMenuItemTags(menuData.menu_item_tags);
                setAllMenuSectionTypes(menuData.menu_types);
                setAllMenuSections(menuData.menus);

                // Group menu sections by type
                const sectionsByType = menuData.menu_types.map(type => ({
                    type: type.id,
                    name: type.name,
                    sections: menuData.menus.filter(section => section.menu_types.some(menuType => menuType.id === type.id))
                }));

                setMenuSectionsByType(sectionsByType);
                
                // Set initial active menu section type and id
                if (menuData.menus.length > 0) {
                    setActiveMenuSectionId(menuData.menus[0].id);
                    if (menuData.menu_types.length > 0 && menuData.menus[0].menu_types.length > 0) {
                        setActiveMenuSectionType(menuData.menus[0].menu_types[0].id);
                    }
                }

                // Simulate a minimum loading time to prevent flash of content
                await new Promise(resolve => setTimeout(resolve, 50));
            } catch (error) {
                console.error('Error initializing menu data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        initializeMenuData();
    }, [menuData]);

    useEffect(() => {
        if (allMenuSections.length > 0 && !activeMenuSectionId) {
            setActiveMenuSectionId(allMenuSections[0].id);
        }
    }, [allMenuSections, activeMenuSectionId]);

    useEffect(() => {
        const handleScroll = () => {
            const newOpacity = Math.max(0, 1 - window.scrollY / 100); // Calculate new opacity as we scroll
            setHeaderOpacity(newOpacity);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1 // Adjust threshold as needed
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const sectionId = parseInt(entry.target.getAttribute('data-section-id') || '0');
                    setActiveMenuSectionId(sectionId);
                    
                    // Find the menu section type that contains the active section
                    const activeType = menuSectionsByType.find(type => 
                        type.sections.some(section => section.id === sectionId)
                    );
                    
                    if (activeType) {
                        setActiveMenuSectionType(activeType.type);
                    }
                }
            });
        };

        if (!isLoading && allMenuSections.length > 0) {
            const observer = new IntersectionObserver(observerCallback, observerOptions);

            allMenuSections.forEach(section => {
                const ref = sectionRefs.current[section.id];
                if (ref) {
                    observer.observe(ref);
                }
            });

            return () => {
                allMenuSections.forEach(section => {
                    const ref = sectionRefs.current[section.id];
                    if (ref) {
                        observer.unobserve(ref);
                    }
                });
            };
        }
    }, [allMenuSections, isLoading, menuSectionsByType]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isDietaryPopupActive) {
                handleCloseDietaryFiltersPopup();
            }
        };

        if (isDietaryPopupActive) {
            document.addEventListener('keydown', handleKeyDown);
            // Trap focus inside the popup
            if (dietaryPopupRef.current) {
                const focusableElements = dietaryPopupRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                const trapFocus = (e: KeyboardEvent) => {
                    if (e.key === 'Tab') {
                        if (e.shiftKey) { // Shift + Tab
                            if (document.activeElement === firstElement) {
                                e.preventDefault();
                                lastElement.focus();
                            }
                        } else { // Tab
                            if (document.activeElement === lastElement) {
                                e.preventDefault();
                                firstElement.focus();
                            }
                        }
                    }
                };

                document.addEventListener('keydown', trapFocus);

                firstElement?.focus();

                return () => {
                    document.removeEventListener('keydown', trapFocus);
                };
            }
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isDietaryPopupActive]);

    const handleFilterClick = (tagId: string) => {
        setActiveFilters(prevFilters => {
            if (prevFilters.includes(tagId)) {
                return prevFilters.filter(id => id !== tagId);
            } else {
                return [...prevFilters, tagId];
            }
        });
    };

    const handleMenuSectionTypeClick = (typeId: number) => {
        setActiveMenuSectionType(typeId);
    };

    const handleDietaryFiltersClick = () => {
        setIsDietaryPopupActive(true);
    };

    const handleCloseDietaryFiltersPopup = () => {
        setIsDietaryPopupActive(false);
        openDietaryButtonRef.current?.focus();
    };

    const toggleMenu = () => {
        setIsMenuOpen(prevState => !prevState);
    };

    return (
        <div className="m__menus | min-h-screen">
            <div className="m__menus-header | sticky z-40 top-[-90px] xl:top-[-130px] w-full bg-red pt-28">
                <div className="m__menus-header-title-wrapper | u__wrap pb-3 xl:py-6 bg-red flex xl:block">
                    <h1 className="m__menus-header-title | flex-grow text-white text-[65px] xl:text-[126px] font-extrabold font-title uppercase leading-[52px] xl:leading-[98px]" style={{ opacity: headerOpacity }}>
                        {module.title}
                    </h1>

                    <div className="hidden grid items-center">
                        <button
                            ref={openDietaryButtonRef}
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded={isDietaryPopupActive}
                            className="button-black js__open-dietary-filters"
                            onClick={(e) => {
                                e.preventDefault();
                                handleDietaryFiltersClick();
                            }}
                        >
                            Dietary Filters
                        </button>

                        <div
                            className={`dietary-filters-popup | fixed w-screen h-screen top-0 left-0 z-50 bg-red py-4 u__wrap grid items-start pt-[100px] ${isDietaryPopupActive ? 'active' : ''}`}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="dietary-filters-title"
                            ref={dietaryPopupRef}
                            tabIndex={-1}
                        >
                            <div className="bg-white relative p-5 text-center">
                                <button
                                    type="button"
                                    className="absolute top-5 right-5 cursor-pointer w-[20px] h-[20px]"
                                    aria-label="Close Dietary Filters"
                                    onClick={handleCloseDietaryFiltersPopup}
                                >
                                    <img src={popupCloseIcon.src} alt="Close Popup icon" />
                                </button>
                                <h2 id="dietary-filters-title" className="text-black text-[22px] font-medium font-copy capitalize leading-snug">Filter By:</h2>
                                <p className="text-black text-[17px] font-normal font-copy leading-[25px] tracking-wide mb-3">Click to filter the menu by dietary requirements</p>
                                <div>
                                    {allMenuItemTags.map((tag) => (
                                        <button
                                            key={tag.id}
                                            type="button"
                                            className={`filter-toggle | bg-[#e5dacf] rounded-[30px] overflow-hidden py-3 px-5 flex mb-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red ${activeFilters.includes(tag.id.toString()) ? 'active' : ''}`}
                                            aria-pressed={activeFilters.includes(tag.id.toString())}
                                            onClick={() => {
                                                handleFilterClick(tag.id.toString());
                                            }}
                                        >
                                            <span className="toggle-pin | flex-none bg-white w-[35px] h-[35px] rounded-full border border-[#e21837]"></span>
                                            <span className="toggle-label | flex-grow text-black text-xl font-medium font-copy capitalize leading-snug">{tag.name}</span>
                                            <span className="toggle-pin-active | flex-none bg-red w-[35px] h-[35px] rounded-full border border-[#e21837]"></span>
                                        </button>
                                    ))}
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="large-button mt-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleCloseDietaryFiltersPopup();
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="m__menus-header-mobile-menu-picker | xl:hidden p-2 bg-black text-center">
                    <button
                        type="button"
                        className="w-full text-white text-xl font-medium font-copy capitalize leading-none flex items-center justify-center"
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        onClick={toggleMenu}
                    >
                        Menu Filter <img className={`inline w-[17px] h-[17px] ml-1`} src={plusIcon.src} alt="Plus icon" />
                    </button>
                </div>
                <div className="m__menus-header-actions | hidden xl:block u__wrap bg-black py-2">
                    <div className="m__menus-header-actions-inner | flex items-center">
                        <div className="m__menus-header-filters | hidden flex-none items-center gap-x-3">
                            <span className="text-white text-[17px] font-normal font-copy leading-relaxed">Filter By:</span>
                            {allMenuItemTags.map((tag) => (
                                <button
                                    key={tag.id}
                                    type="button"
                                    className={`button-outline js-menu-filter ${activeFilters.includes(tag.id.toString()) ? 'active' : ''}`}
                                    aria-pressed={activeFilters.includes(tag.id.toString())}
                                    data-tag-id={tag.id}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFilterClick(tag.id.toString());
                                    }}
                                >
                                    {tag.name}
                                </button>
                            ))}
                        </div>
                        <div className="m__menus-header-buttons | flex-grow flex gap-x-3 items-center justify-end">
                            {module.buttons.map((button, index) => (
                                <Button 
                                    key={index}
                                    button={{
                                        url: button.button.url,
                                        title: button.button.title,
                                        class: 'button text-center',
                                        target: button.button.target
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="m__menus-header-buttons-mobile | u__wrap grid xl:hidden grid-cols-2 gap-x-3 items-center justify-center pt-4">
                {module.buttons.map((button, index) => (
                    <Button 
                        key={index}
                        button={{
                            url: button.button.url,
                            title: button.button.title,
                            class: 'button text-center mb-2',
                            target: button.button.target
                        }}
                    />
                ))}
            </div>

            <div className="m__menus-content | xl:pt-8">
                <div className="u__wrap xl:flex xl:gap-x-20">
                    <nav
                        className={`m__menus-content-types ${isMenuOpen ? 'active' : ''}`}
                        aria-label="Menu Sections"
                    >
                        <div className="m__menus-content-types-inner | fixed w-full xl:w-auto h-full xl:h-auto xl:sticky top-0 left-0 xl:left-auto xl:top-[210px] z-40 xl:z-10 min-w-[300px] flex-none bg-red xl:bg-[#e5dacf] xl:rounded-[20px] pt-20 lg:pt-24 xl:pt-0 pb-4 z-30">
                            
                            <h3 className="m__menus-content-types-title | hidden xl:block px-6 pt-2 pb-2 mb-2 border-b border-[#aba5a0] text-black text-base font-normal font-copy leading-normal">Filter By:</h3>

                            <button
                                type="button"
                                className="m__menus-content-types-inner-toggle | xl:hidden p-2 bg-black text-white w-full text-[17px] leading-[17px] font-medium    font-copy text-center mb-4 flex items-center justify-center"
                                aria-label="Close Menu Filter"
                                onClick={toggleMenu}
                            >
                                Close <img className={`inline w-[17px] h-[17px] ml-1 rotate-45`} src={plusIcon.src} alt="Close icon" />
                            </button>
                            
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                menuSectionsByType.length > 0 ? (
                                    menuSectionsByType.map((menuSectionType: any, index: number) => (
                                        <div 
                                            key={menuSectionType.type} 
                                            className={`m__menus-content-types-type-group ${activeMenuSectionType === menuSectionType.type ? 'active' : ''}`} 
                                            data-type={menuSectionType.type}
                                        >
                                            <button
                                                type="button"
                                                className="m__menus-content-types-type-title | flex px-6 cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red"
                                                aria-expanded={activeMenuSectionType === menuSectionType.type}
                                                onClick={() => handleMenuSectionTypeClick(menuSectionType.type)}
                                            >
                                                <span className="flex-grow text-white xl:text-black text-[26px] font-medium font-copy capitalize leading-[39px]">{menuSectionType.name}</span>
                                                <img src={plusIcon.src} alt="Toggle icon" className="flex-none" />
                                            </button>
                                            <div
                                                className={`m__menus-content-types-type-group-children ${activeMenuSectionType === menuSectionType.type ? 'block' : 'hidden'}`}
                                            >
                                                {menuSectionType.sections && menuSectionType.sections.length > 0 ? (
                                                    menuSectionType.sections.map((menuSection: any) => (
                                                        <button
                                                            key={menuSection.id}
                                                            type="button"
                                                            className={`m__menus-content-types-item | flex items-center px-6 py-1 gap-x-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-red ${activeMenuSectionId === menuSection.id ? 'active' : ''}`}
                                                            data-section-id={menuSection.id}
                                                            onClick={() => {
                                                                const sectionRef = sectionRefs.current[menuSection.id];
                                                                if (sectionRef) {
                                                                    sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start', inline: "nearest" });
                                                                    toggleMenu();
                                                                }
                                                            }}
                                                        >
                                                            <img src={faqCatIcon.src} alt="Active icon" className="inline-block flex-none w-[17px]" />
                                                            <span className="m__faqs-cats-item-name | flex-grow text-white xl:text-black text-lg font-bold font-mono capitalize leading-[30px]" data-section-id={menuSection.id}>
                                                                {menuSection.display_name || menuSection.title}
                                                            </span>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <p className="px-6 py-1">No Menu Sections Found</p>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="px-6 py-1">Loading menu sections...</p>
                                )
                            )}
                        </div>
                    </nav>
                    <main className="m__menus-content-sections | relative xl:grow xl:min-w-0 pt-8 xl:pt-0">
                        {isLoading ? (
                           <Spinner />
                        ) : (
                            allMenuSections.map((section) => (
                                <div
                                    key={section.id}
                                    data-section-id={section.id}
                                    ref={(el) => (sectionRefs.current[section.id] = el)}
                                    className="menu-section-wrapper | mb-8"
                                >
                                    <MenuSection 
                                        menuSection={section}
                                        activeFilters={activeFilters}
                                    />
                                </div>
                            ))
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Menus;

